/* eslint-disable @typescript-eslint/no-explicit-any */
import qs from 'qs'
import axios from 'axios'
import i18n from 'src/locales'

import { refreshToken } from 'src/services'
import { decrypt, encrypt } from 'src/utils/storage'
import {
  SAP_API,
  HOST_API,
  IMPORT_API,
  STORAGE_KEY,
  REFRESH_TOKEN_KEY,
} from 'src/constants'
import { paths } from 'src/routes/paths'
import toast from 'react-hot-toast'

const EXPIRED_TOKEN_STATUS = 401

export const infocenterGetTokenUrl =
  'https://infocentre.pli-petronas.com/account/LoginGetToken'

let refreshTokenPromise: Promise<any> | null = null

const handleUnauthorizedError = () => {
  const currentLanguage = localStorage.getItem('i18nextLng')

  localStorage.clear()
  if (currentLanguage) {
    localStorage.setItem('i18nextLng', currentLanguage)
  }

  toast.error(i18n.t('error.sessionExpired'))

  setTimeout(() => {
    window.location.href = paths.auth.login
  }, 1500)
}

const checkTokenAndRefresh = async (error: any) => {
  const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY)
  const authType = localStorage.getItem('authType')

  if (error.config._retry) {
    return Promise.reject(error)
  }

  if (authType === 'msal') {
    return Promise.reject(error)
  }

  if (
    error.response &&
    error.response.status === EXPIRED_TOKEN_STATUS &&
    refreshTokenValue
  ) {
    if (!refreshTokenPromise) {
      refreshTokenPromise = refreshToken()
        .then((newToken) => {
          if (!newToken) {
            return Promise.reject(new Error('No token received from refresh'))
          }

          localStorage.setItem(STORAGE_KEY, encrypt(newToken))
          refreshTokenPromise = null
          return newToken
        })
        .catch((err) => {
          const currentLanguage = localStorage.getItem('i18nextLng')

          localStorage.clear()
          if (currentLanguage) {
            localStorage.setItem('i18nextLng', currentLanguage)
          }

          toast.error(i18n.t('error.sessionExpired'))
          setTimeout(() => {
            window.location.href = paths.auth.login
          }, 1500)

          refreshTokenPromise = null
          return Promise.reject(err)
        })
    }

    return refreshTokenPromise.then((token) => {
      if (!token) {
        return Promise.reject(error)
      }

      error.config._retry = true
      error.config.headers.Authorization = `Bearer ${token}`
      return axios.request(error.config)
    })
  }

  if (error.response && error.response.status === EXPIRED_TOKEN_STATUS) {
    handleUnauthorizedError()
  }

  return Promise.reject(error)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setHeaders = (config: any) => {
  config.headers['Accept-Language'] = i18n.language ?? 'en-US'
  const token = localStorage.getItem(STORAGE_KEY)

  if (token) {
    config.headers.Authorization = `Bearer ${decrypt(token)}`
  }
  return config
}

//------------------------------------------- BASE API
export const baseApi = axios.create({
  baseURL: HOST_API,
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' })
  },
})

baseApi.interceptors.request.use(setHeaders, (error) => Promise.reject(error))
baseApi.interceptors.response.use((response) => response, checkTokenAndRefresh)

delete baseApi.defaults.headers.common['Content-Type']
delete baseApi.defaults.headers.post['Content-Type']
delete baseApi.defaults.headers.put['Content-Type']
delete baseApi.defaults.headers.patch['Content-Type']

//------------------------------------------- IMPORT API
export const importApi = axios.create({
  baseURL: IMPORT_API,
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' })
  },
})

importApi.interceptors.request.use(setHeaders, (error) => Promise.reject(error))
importApi.interceptors.response.use(
  (response) => response,
  checkTokenAndRefresh
)

delete importApi.defaults.headers.common['Content-Type']
delete importApi.defaults.headers.post['Content-Type']
delete importApi.defaults.headers.put['Content-Type']
delete importApi.defaults.headers.patch['Content-Type']

//------------------------------------------- SAP API
export const sapApi = axios.create({
  baseURL: SAP_API,
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' })
  },
})

sapApi.interceptors.request.use(setHeaders, (error) => Promise.reject(error))
sapApi.interceptors.response.use((response) => response, checkTokenAndRefresh)

delete sapApi.defaults.headers.common['Content-Type']
delete sapApi.defaults.headers.post['Content-Type']
delete sapApi.defaults.headers.put['Content-Type']
delete sapApi.defaults.headers.patch['Content-Type']
